import {
  CATEGORY_COUNT,
  CROWN_SEGMENT_INDEX,
  TRIVIA_CATEGORY_KEYS,
  type TriviaCategoryKey,
} from "./catalog";
import type { TriviaMatchState } from "./matchTypes";

function seededRng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    return (h >>> 0) / 4294967296;
  };
}

export function createInitialTriviaState(
  playerIds: string[],
  matchSeed: string,
  opts?: { firstTurnPlayerId?: string },
): TriviaMatchState {
  const sorted = [...playerIds].sort((a, b) => a.localeCompare(b));
  const first = opts?.firstTurnPlayerId;
  const firstIdx =
    typeof first === "string" && first.length > 0
      ? sorted.indexOf(first)
      : 0;
  const currentPlayerIndex = firstIdx >= 0 ? firstIdx : 0;
  return {
    playerIds: sorted,
    currentPlayerIndex,
    scores: Object.fromEntries(sorted.map((p) => [p, 0])),
    wedges: Object.fromEntries(sorted.map((p) => [p, [] as string[]])),
    phase: "spin",
    activeCategory: null,
    activeQuestionId: null,
    questionIdsUsed: [],
    matchSeed,
    spinNonce: 0,
    spinSegmentIndex: null,
    lastResult: null,
  };
}

/** Wheel slice indices the current player can still land on (incomplete categories + crown). */
function eligibleSpinSegmentIndices(state: TriviaMatchState): number[] {
  const pid = state.playerIds[state.currentPlayerIndex];
  if (!pid) return [...Array(CATEGORY_COUNT + 1).keys()];
  const have = new Set(state.wedges[pid] ?? []);
  const out: number[] = [];
  for (let i = 0; i < CATEGORY_COUNT; i++) {
    const key = TRIVIA_CATEGORY_KEYS[i]!;
    if (!have.has(key)) out.push(i);
  }
  out.push(CROWN_SEGMENT_INDEX);
  return out;
}

export function computeSpinSegment(
  state: TriviaMatchState,
  matchId: string,
): number {
  const rng = seededRng(
    `${state.matchSeed}:${matchId}:spin:${state.spinNonce}:${state.playerIds[state.currentPlayerIndex]}`,
  );
  const eligible = eligibleSpinSegmentIndices(state);
  if (eligible.length === 0) return CROWN_SEGMENT_INDEX;
  return eligible[Math.floor(rng() * eligible.length)]!;
}

export function segmentToCategory(segment: number): TriviaCategoryKey {
  const idx = Math.max(0, Math.min(CATEGORY_COUNT - 1, segment));
  return TRIVIA_CATEGORY_KEYS[idx]!;
}

export function pickCategoryForCrownChoice(
  state: TriviaMatchState,
  playerId: string,
  rng: () => number,
): TriviaCategoryKey {
  const have = new Set(state.wedges[playerId] ?? []);
  const missing = TRIVIA_CATEGORY_KEYS.filter((k) => !have.has(k));
  const pool = missing.length > 0 ? missing : [...TRIVIA_CATEGORY_KEYS];
  return pool[Math.floor(rng() * pool.length)]!;
}

export function applySpinLandCrown(params: {
  state: TriviaMatchState;
  segmentIndex: number;
}): TriviaMatchState {
  const state = structuredClone(params.state) as TriviaMatchState;
  state.phase = "choose_category";
  state.spinSegmentIndex = params.segmentIndex;
  state.activeCategory = null;
  state.activeQuestionId = null;
  state.lastResult = null;
  state.spinNonce += 1;
  return state;
}

export function applySpinPlacedQuestion(params: {
  state: TriviaMatchState;
  category: TriviaCategoryKey;
  questionId: string;
  segmentIndex: number;
}): TriviaMatchState {
  const state = structuredClone(params.state) as TriviaMatchState;
  state.phase = "answer";
  state.activeCategory = params.category;
  state.activeQuestionId = params.questionId;
  state.spinSegmentIndex = params.segmentIndex;
  state.questionIdsUsed = [...state.questionIdsUsed, params.questionId];
  state.spinNonce += 1;
  state.lastResult = null;
  delete state.answerRound;
  return state;
}

export function applyCrownCategoryPlacedQuestion(params: {
  state: TriviaMatchState;
  category: TriviaCategoryKey;
  questionId: string;
}): TriviaMatchState {
  const state = structuredClone(params.state) as TriviaMatchState;
  if (state.phase !== "choose_category") return state;
  state.phase = "answer";
  state.activeCategory = params.category;
  state.activeQuestionId = params.questionId;
  state.questionIdsUsed = [...state.questionIdsUsed, params.questionId];
  state.lastResult = null;
  delete state.answerRound;
  return state;
}

export const DUEL_TIME_POWER_SECONDS = 15;

export function pickTwoWrongChoiceIndexes(correctIndex: number): [number, number] {
  const wrong = [0, 1, 2, 3].filter((i) => i !== correctIndex);
  const a = wrong[0]!;
  const b = wrong[1]!;
  return a < b ? [a, b] : [b, a];
}

export function applyPowerUpFiftyToState(
  state: TriviaMatchState,
  correctIndex: number,
): TriviaMatchState | null {
  if (state.phase !== "answer") return null;
  const cur = state.answerRound;
  if (cur?.fiftyUsed || (cur?.eliminatedChoiceIndexes?.length ?? 0) > 0) {
    return null;
  }
  const next = structuredClone(state) as TriviaMatchState;
  const [x, y] = pickTwoWrongChoiceIndexes(correctIndex);
  next.answerRound = {
    ...next.answerRound,
    fiftyUsed: true,
    eliminatedChoiceIndexes: [x, y],
  };
  return next;
}

export function applyPowerUpTimeToState(state: TriviaMatchState): TriviaMatchState | null {
  if (state.phase !== "answer") return null;
  if (state.answerRound?.timeUsed) return null;
  const next = structuredClone(state) as TriviaMatchState;
  const prev = next.answerRound?.extraSecondsGranted ?? 0;
  next.answerRound = {
    ...next.answerRound,
    timeUsed: true,
    extraSecondsGranted: prev + DUEL_TIME_POWER_SECONDS,
  };
  return next;
}

export function applySkipToNewQuestionState(
  state: TriviaMatchState,
  newQuestionId: string,
): TriviaMatchState | null {
  if (state.phase !== "answer" || !state.activeCategory) return null;
  const next = structuredClone(state) as TriviaMatchState;
  next.activeQuestionId = newQuestionId;
  next.questionIdsUsed = [...next.questionIdsUsed, newQuestionId];
  delete next.answerRound;
  return next;
}

export function playerHasAllWedges(
  wedges: Record<string, string[]>,
  playerId: string,
): boolean {
  const w = new Set(wedges[playerId] ?? []);
  for (const c of TRIVIA_CATEGORY_KEYS) {
    if (!w.has(c)) return false;
  }
  return true;
}

/**
 * Win/loss for finished duels: the engine ends the match when someone completes all
 * category wedges — not when they merely lead on points (repeat corrects in owned
 * categories still add score without a new wedge).
 */
export function triviaDuelOutcomeForViewer(params: {
  wedges: Record<string, string[]> | undefined;
  scores: Record<string, number>;
  myId: string;
  rivalId: string;
}): "win" | "loss" | "draw" {
  const { wedges, scores, myId, rivalId } = params;
  const w = wedges ?? {};
  if (myId && rivalId) {
    const iAll = playerHasAllWedges(w, myId);
    const rAll = playerHasAllWedges(w, rivalId);
    if (iAll && !rAll) return "win";
    if (rAll && !iAll) return "loss";
    if (iAll && rAll) return "draw";
  }
  const ys = scores[myId] ?? 0;
  const rs = scores[rivalId] ?? 0;
  if (ys > rs) return "win";
  if (ys < rs) return "loss";
  return "draw";
}

export function applyAnswer(params: {
  state: TriviaMatchState;
  playerId: string;
  correct: boolean;
  category: TriviaCategoryKey;
}): TriviaMatchState {
  const state = structuredClone(params.state) as TriviaMatchState;
  const { playerId, correct, category } = params;
  const pids = state.playerIds;
  const idx = pids.indexOf(playerId);
  if (idx < 0) return state;

  const ac = { ...(state.answerCounts ?? {}) };
  const prev = ac[playerId] ?? { attempted: 0, correct: 0 };
  ac[playerId] = {
    attempted: prev.attempted + 1,
    correct: prev.correct + (correct ? 1 : 0),
  };
  state.answerCounts = ac;

  delete state.answerRound;
  state.lastResult = { playerId, correct, category };

  const streaks = { ...(state.answerStreaks ?? {}) };
  if (correct) streaks[playerId] = (streaks[playerId] ?? 0) + 1;
  else streaks[playerId] = 0;
  state.answerStreaks = streaks;

  if (correct) {
    state.scores[playerId] = (state.scores[playerId] ?? 0) + 10;
    const cur = new Set(state.wedges[playerId] ?? []);
    if (!cur.has(category)) {
      cur.add(category);
      state.wedges[playerId] = [...cur];
    }
    if (playerHasAllWedges(state.wedges, playerId)) {
      state.phase = "spin";
      state.activeCategory = null;
      state.activeQuestionId = null;
      state.spinSegmentIndex = null;
      return state;
    }
    state.phase = "spin";
    state.activeCategory = null;
    state.activeQuestionId = null;
    state.spinSegmentIndex = null;
    return state;
  }

  const nextIdx = (idx + 1) % pids.length;
  state.currentPlayerIndex = nextIdx;
  state.phase = "spin";
  state.activeCategory = null;
  state.activeQuestionId = null;
  state.spinSegmentIndex = null;
  return state;
}
